import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { historyApi, type RecordWatchInput, type WatchEntry } from '@/services/historyApi';
import { ApiClientError } from '@/services/apiClient';
import { logout } from './authSlice';
import { posthog } from '@/observability/posthog';

const STORAGE_KEY = 'cobstream:history';
export const HISTORY_LIMIT = 20;

export type SourceOfTruth = 'local' | 'server';
export type HistoryStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface HistoryState {
  entries: WatchEntry[];
  sourceOfTruth: SourceOfTruth;
  status: HistoryStatus;
  error: string | null;
  /** True while a drain-local-to-server merge is in progress. */
  merging: boolean;
}

function describeError(err: unknown): string {
  if (err instanceof ApiClientError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

function loadLocal(): WatchEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidEntry).slice(0, HISTORY_LIMIT);
  } catch {
    return [];
  }
}

function saveLocal(entries: WatchEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage quota / disabled — nothing we can do here.
  }
}

function clearLocal(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function isValidEntry(e: unknown): e is WatchEntry {
  if (typeof e !== 'object' || e === null) return false;
  const r = e as Record<string, unknown>;
  const hasMovie = typeof r.movieId === 'number' && r.movieId > 0;
  const hasTv = typeof r.tvId === 'number' && r.tvId > 0;
  const hasAnime = typeof r.animeMalId === 'number' && r.animeMalId > 0;
  const hasSportsVod = typeof r.sportsVodId === 'string' && r.sportsVodId.length > 0;
  const presentCount = (hasMovie ? 1 : 0) + (hasTv ? 1 : 0) + (hasAnime ? 1 : 0) + (hasSportsVod ? 1 : 0);
  if (presentCount !== 1) return false;
  if (typeof r.progress !== 'number' || r.progress < 0) return false;
  if (typeof r.lastUpdated !== 'string') return false;
  return true;
}

/**
 * Smart upsert: replaces the existing entry with the matching identity
 * (same movieId for movies, same tvId for series), or inserts at the head.
 * Caps the result at HISTORY_LIMIT, evicting the oldest entries.
 */
export function upsertEntry(entries: WatchEntry[], input: RecordWatchInput): WatchEntry[] {
  const isMovie = 'movieId' in input && typeof input.movieId === 'number';
  const isAnime = 'animeMalId' in input && typeof input.animeMalId === 'number';
  const isSportsVod = 'sportsVodId' in input && typeof input.sportsVodId === 'string';
  const isTv = !isMovie && !isAnime && !isSportsVod;
  const next: WatchEntry = {
    movieId: isMovie ? input.movieId! : null,
    tvId: isTv ? input.tvId! : null,
    animeMalId: isAnime ? input.animeMalId! : null,
    sportsVodId: isSportsVod ? input.sportsVodId! : null,
    seasonNumber: isTv ? input.seasonNumber ?? null : null,
    episodeNumber: isTv || isAnime ? input.episodeNumber ?? null : null,
    animeAudio: isAnime ? input.animeAudio ?? null : null,
    progress: input.progress,
    lastUpdated: new Date().toISOString(),
  };

  const filtered = entries.filter((e) => {
    if (isMovie) return e.movieId !== next.movieId;
    if (isAnime) return e.animeMalId !== next.animeMalId;
    if (isSportsVod) return e.sportsVodId !== next.sportsVodId;
    return e.tvId !== next.tvId;
  });

  // Newest first via prepend; cap at the per-user limit. We rely on insertion
  // order rather than a timestamp sort so that ties (multiple writes in the
  // same ms) are deterministic — the just-recorded entry always wins.
  return [next, ...filtered].slice(0, HISTORY_LIMIT);
}

function recordInputForEntry(e: WatchEntry): RecordWatchInput | null {
  if (e.movieId !== null) {
    return { movieId: e.movieId, progress: e.progress };
  }
  if (e.tvId !== null && e.seasonNumber !== null && e.episodeNumber !== null) {
    return {
      tvId: e.tvId,
      seasonNumber: e.seasonNumber,
      episodeNumber: e.episodeNumber,
      progress: e.progress,
    };
  }
  if (e.animeMalId !== null && e.episodeNumber !== null && e.animeAudio !== null) {
    return {
      animeMalId: e.animeMalId,
      episodeNumber: e.episodeNumber,
      animeAudio: e.animeAudio,
      progress: e.progress,
    };
  }
  if (e.sportsVodId !== null) {
    return { sportsVodId: e.sportsVodId, progress: e.progress };
  }
  return null;
}

// ---------- thunks ----------

export const fetchServerHistory = createAsyncThunk<WatchEntry[], void, { rejectValue: string }>(
  'history/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const { entries } = await historyApi.list();
      return entries;
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

type RecordResult =
  | { mode: 'server'; entry: WatchEntry }
  | { mode: 'local'; input: RecordWatchInput };

/**
 * Keys for which `watch_started` has already fired this session. Without
 * this guard, `recordWatch` is called on every throttled `timeupdate`
 * (~every 10s) and `watch_started` was being captured 6+ times per
 * minute of viewing per session — corrupting funnel/retention analysis
 * that assumed the event fires once per (user, title, episode).
 *
 * Module-scope so it persists across thunk dispatches within a page
 * session. A page refresh resets it, which matches the analytics intent
 * ("a new 'started watching' moment"). Exported for tests.
 */
export const __watchStartedKeys = new Set<string>();

/** Test/debug only — reset the fire-once guard between cases. */
export function __resetWatchStartedKeys(): void {
  __watchStartedKeys.clear();
}

function watchStartedKey(input: RecordWatchInput): string {
  if (typeof input.movieId === 'number') return `movie:${input.movieId}`;
  if (typeof input.animeMalId === 'number') return `anime:${input.animeMalId}:${input.episodeNumber ?? 0}:${input.animeAudio ?? ''}`;
  if (typeof input.sportsVodId === 'string') return `sports-vod:${input.sportsVodId}`;
  return `tv:${input.tvId}:${input.seasonNumber ?? 0}:${input.episodeNumber ?? 0}`;
}

export const recordWatch = createAsyncThunk<RecordResult, RecordWatchInput, { rejectValue: string; state: { history: HistoryState } }>(
  'history/record',
  async (input, { getState, rejectWithValue }) => {
    try {
      const isMovie = typeof input.movieId === 'number';
      const isSportsVod = typeof input.sportsVodId === 'string';
      // Fire `watch_started` AT MOST ONCE per (movie / tv:s:e / anime / sports-vod)
      // per session. Without this guard the event was emitted every 10s alongside
      // throttled history writes (see `Watch.tsx#onTimeUpdate`).
      const key = watchStartedKey(input);
      if (!__watchStartedKeys.has(key)) {
        __watchStartedKeys.add(key);
        if (isSportsVod) {
          posthog.captureEvent('watch_started', {
            sports_vod_id: input.sportsVodId,
            is_movie: false,
            source: input.source ?? 'history',
          });
        } else {
          posthog.captureEvent('watch_started', {
            tmdb_id: (isMovie ? input.movieId : input.tvId) as number,
            is_movie: isMovie,
            source: input.source ?? 'history',
          });
        }
      }

      const source = getState().history.sourceOfTruth;
      if (source === 'server') {
        const { entry } = await historyApi.record(input);
        return { mode: 'server', entry };
      }
      return { mode: 'local', input };
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

export const mergeLocalHistoryIntoServer = createAsyncThunk<WatchEntry[], void, { rejectValue: string; state: { history: HistoryState } }>(
  'history/merge',
  async (_, { getState, rejectWithValue }) => {
    try {
      const local = getState().history.entries;
      // Drain newest-first so if any single POST fails the recent entries get
      // through. Per-row failures are swallowed — they're best-effort.
      for (const entry of [...local].sort((a, b) => (a.lastUpdated < b.lastUpdated ? 1 : -1))) {
        const input = recordInputForEntry(entry);
        if (!input) continue;
        try {
          await historyApi.record(input);
        } catch {
          // Best-effort: a per-row failure shouldn't abort the rest of the merge.
        }
      }
      const { entries } = await historyApi.list();
      return entries;
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

// ---------- slice ----------

const initialState: HistoryState = {
  entries: loadLocal(),
  sourceOfTruth: 'local',
  status: 'idle',
  error: null,
  merging: false,
};

const historySlice = createSlice({
  name: 'history',
  initialState,
  reducers: {
    /** Test/debug only — drop everything back to local mode without touching auth. */
    resetToLocal(state) {
      state.entries = loadLocal();
      state.sourceOfTruth = 'local';
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Logout from authSlice → drop server cache, fall back to whatever's in localStorage.
      .addCase(logout, (state) => {
        state.entries = loadLocal();
        state.sourceOfTruth = 'local';
        state.status = 'idle';
        state.error = null;
        state.merging = false;
      })
      .addCase(fetchServerHistory.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchServerHistory.fulfilled, (state, action) => {
        state.entries = action.payload;
        state.sourceOfTruth = 'server';
        state.status = 'ready';
        // Wipe the local cache so a future logout starts clean. (We can re-cache
        // locally on demand later if cross-device sync becomes a feature.)
        clearLocal();
      })
      .addCase(fetchServerHistory.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? action.error.message ?? 'Failed to load history';
      })
      .addCase(recordWatch.fulfilled, (state, action: PayloadAction<RecordResult>) => {
        if (action.payload.mode === 'local') {
          state.entries = upsertEntry(state.entries, action.payload.input);
          saveLocal(state.entries);
        } else {
          // Server mode — replace the matching entry by id; collapse duplicates by identity.
          const entry = action.payload.entry;
          const filtered = state.entries.filter((e) => {
            if (entry.movieId !== null) return e.movieId !== entry.movieId;
            if (entry.tvId !== null) return e.tvId !== entry.tvId;
            if (entry.animeMalId !== null) return e.animeMalId !== entry.animeMalId;
            if (entry.sportsVodId !== null) return e.sportsVodId !== entry.sportsVodId;
            return true;
          });
          state.entries = [entry, ...filtered].slice(0, HISTORY_LIMIT);
        }
      })
      .addCase(recordWatch.rejected, (state, action) => {
        state.error = action.payload ?? action.error.message ?? 'Failed to record watch';
      })
      .addCase(mergeLocalHistoryIntoServer.pending, (state) => {
        state.merging = true;
        state.error = null;
      })
      .addCase(mergeLocalHistoryIntoServer.fulfilled, (state, action) => {
        state.entries = action.payload;
        state.sourceOfTruth = 'server';
        state.merging = false;
        state.status = 'ready';
        clearLocal();
      })
      .addCase(mergeLocalHistoryIntoServer.rejected, (state, action) => {
        state.merging = false;
        state.error = action.payload ?? action.error.message ?? 'Failed to sync watch history';
      });
  },
});

export const { resetToLocal } = historySlice.actions;
export default historySlice.reducer;
